/**
 * useSports — KAN-11809
 * ======================
 * DB-driven sport availability. Replaces the previous env-flag-driven
 * `getEnabledSports()` / `getAvailableSports()` helpers, which violated
 * the config-driven architecture mandate (CLAUDE.md "Architecture
 * target": adding a sport = config row insert, not a deploy).
 *
 * Source of truth: `public.sport_config.is_active`. A sport appears on
 * the UI iff (a) its row exists in `sport_config` with `is_active=true`
 * AND (b) the static `SPORT_CONFIGS` map in `types/sports.ts` has the
 * matching periodization / display metadata. Sports the DB knows about
 * but the frontend has no metadata for are silently skipped (logged via
 * Sentry breadcrumb so we notice).
 *
 * Telemetry:
 *  - `sports_loaded` PostHog event on first successful load (count +
 *    sport_ids + source) — surfaces silent config drift in product
 *    analytics.
 *  - Sentry exception if the active-list comes back empty — that's
 *    always a bug (DB outage, RLS regression, or every row was flipped
 *    off by accident).
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { SPORT_CONFIGS, type SportConfig, type SportId } from '../types/sports';
import { captureException, addBreadcrumb } from '../sentry';
import { captureEvent } from '../posthog';

export interface ActiveSportRow {
  sport_id: string;
  display_name: string;
  is_active: boolean;
  predictions_active: boolean;
}

const SPORTS_QUERY_KEY = ['sport-config', 'active'] as const;

/**
 * Raw active-sport rows from `public.sport_config`. Most callers want
 * `useAvailableSportConfigs()` instead — it joins to the static
 * SPORT_CONFIGS metadata.
 */
export function useActiveSports() {
  return useQuery({
    queryKey: SPORTS_QUERY_KEY,
    queryFn: async (): Promise<ActiveSportRow[]> => {
      const { data, error } = await supabase
        .from('sport_config')
        .select('sport_id, display_name, is_active, predictions_active')
        .eq('is_active', true)
        .order('sport_id');
      if (error) throw error;
      const rows = (data ?? []) as ActiveSportRow[];
      if (rows.length === 0) {
        captureException(
          new Error('[useActiveSports] sport_config returned zero active rows'),
          { source: 'useActiveSports' }
        );
      }
      return rows;
    },
    staleTime: 5 * 60_000, // 5 min — config rarely changes
    refetchOnWindowFocus: false,
  });
}

/**
 * Active sports joined to static SPORT_CONFIGS metadata. This is the
 * source the UI should use when rendering sport chips / tabs / filters.
 */
export function useAvailableSportConfigs(): {
  sports: SportConfig[];
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useActiveSports();
  const reportedRef = useRef(false);

  const sports = useMemo<SportConfig[]>(() => {
    if (!data) return [];
    const merged: SportConfig[] = [];
    for (const row of data) {
      const cfg = SPORT_CONFIGS[row.sport_id as SportId];
      if (cfg) {
        merged.push(cfg);
      } else {
        addBreadcrumb(
          `[useAvailableSportConfigs] DB sport_id=${row.sport_id} active but no SPORT_CONFIGS metadata`,
          { sport_id: row.sport_id }
        );
      }
    }
    return merged;
  }, [data]);

  useEffect(() => {
    if (!data || reportedRef.current) return;
    reportedRef.current = true;
    captureEvent('sports_loaded', {
      count: sports.length,
      sport_ids: sports.map((s) => s.id),
      raw_db_count: data.length,
      source: 'sport_config_table',
    });
  }, [data, sports]);

  return {
    sports,
    isLoading,
    error: (error as Error | null) ?? null,
  };
}
